import type { ComposerTranslation } from 'vue-i18n'
import { readCssVar } from '@/utils/chart-theme-colors'
import {
  ApiThreatType,
  BlockDirection,
  SafeEventHandleLogAction,
  SecurityDisposeResult,
  SecurityDisposeStatus,
  SecurityEventLevel,
} from '@/views/dashboard/safe-protection/security-event/constants/enums'
import {
  getEventDescByEventId,
  getEventTypeFilterOptions,
  getEventTypeName,
  resolveSecurityEventTypeLabel,
} from '@/views/dashboard/safe-protection/security-event/event-type-map'
import { getAttackTacticFilterOptions, getAttackTacticName } from '@/views/dashboard/safe-protection/security-event/attack-tactic-map'
import { inferApiThreatTypeFromEntity, parseHandleResultCodes } from '@/views/dashboard/safe-protection/security-event/security-event-detail.mapper'
import type { SafeEventDetailLog, SecurityEventListItem } from '@/views/dashboard/safe-protection/security-event/types'

/** event_level：1-低危, 2-中危, 3-高危（筛选与列表同编码） */
export function trListEventLevel (t: ComposerTranslation, eventLevel?: number) {
  const map: Record<number, string> = {
    [SecurityEventLevel.High]: t('securityEvent.levelHigh'),
    [SecurityEventLevel.Medium]: t('securityEvent.levelMedium'),
    [SecurityEventLevel.Low]: t('securityEvent.levelLow'),
  }
  return eventLevel != null ? map[eventLevel] ?? '--' : '--'
}

export function listLevelColor (eventLevel?: number) {
  if (eventLevel === SecurityEventLevel.High) return 'danger'
  if (eventLevel === SecurityEventLevel.Medium) return 'warning'
  if (eventLevel === SecurityEventLevel.Low) return 'primary'
  return 'info'
}

/** 威胁等级分布饼图：低危=主题色，中危=#FB8F10，高危=#E54545（与详情页 level-* 色一致） */
export function securityEventLevelPieColor (eventLevel?: number) {
  if (eventLevel === SecurityEventLevel.High) return '#E54545'
  if (eventLevel === SecurityEventLevel.Medium) return '#FB8F10'
  if (eventLevel === SecurityEventLevel.Low) {
    return readCssVar('--el-color-primary', '#0456AD')
  }
  return readCssVar('--el-color-primary', '#0456AD')
}

/** @deprecated 与 trListEventLevel 相同，保留别名兼容图表等调用 */
export function trFilterEventLevel (t: ComposerTranslation, level?: SecurityEventLevel) {
  return trListEventLevel(t, level)
}

export function trDisposeStatus (t: ComposerTranslation, s?: number) {
  const map: Record<number, string> = {
    [SecurityDisposeStatus.Pending]: t('securityEvent.statusPending'),
    [SecurityDisposeStatus.Trusted]: t('securityEvent.statusTrusted'),
    [SecurityDisposeStatus.Handled]: t('securityEvent.statusHandled'),
    [SecurityDisposeStatus.Ignored]: t('securityEvent.statusIgnored'),
  }
  return s != null ? map[s] ?? '--' : '--'
}

/** 列表/详情处置状态：el-tag 四种 type */
export function disposeStatusTagType (
  s?: number,
): 'success' | 'info' | 'warning' | 'danger' | undefined {
  if (s === SecurityDisposeStatus.Pending) return 'danger'
  if (s === SecurityDisposeStatus.Handled) return 'success'
  if (s === SecurityDisposeStatus.Ignored) return 'info'
  if (s === SecurityDisposeStatus.Trusted) return 'warning'
  return undefined
}

/** 事件类型 Id（event_type）展示名，数据来自《事件类型ID关系映射》 */
export function trEventType (t: ComposerTranslation, type?: string | number) {
  if (type == null || type === '') return '--'
  const key = `securityEvent.eventType.${type}`
  const text = t(key)
  if (text !== key) return text
  return getEventTypeName(type) ?? String(type)
}

/** 事件 event_id → 事件描述 */
export function trEventDesc (t: ComposerTranslation, eventId?: string | number) {
  if (eventId == null || eventId === '') return '--'
  const key = `securityEvent.eventDesc.${eventId}`
  const text = t(key)
  if (text !== key) return text
  return getEventDescByEventId(eventId) ?? String(eventId)
}

/** @deprecated 使用 trEventDesc */
export const trEventId = trEventDesc

/** 列表事件类型列：按 event_type 映射 */
export function trEventTypeDisplay (
  t: ComposerTranslation,
  row?: { event_type?: string | number; event_id?: string | number },
) {
  if (!row) return '--'
  return trEventType(t, row.event_type)
}

/** 封堵 IP 方向展示（与需求图一致的长文案） */
export function trBlockDirectionLong (t: ComposerTranslation, direction?: number | string) {
  const key = String(direction ?? BlockDirection.Bidirectional)
  const map: Record<string, string> = {
    [String(BlockDirection.Bidirectional)]: 'ipAddressBlocking.directionBidirectionalLong',
    [String(BlockDirection.Inbound)]: 'ipAddressBlocking.directionInboundLong',
    [String(BlockDirection.Outbound)]: 'ipAddressBlocking.directionOutboundLong',
  }
  const i18nKey = map[key]
  if (!i18nKey) return '--'
  const text = t(i18nKey)
  return text !== i18nKey ? text : '--'
}

export function resolveDisposeTitleType (
  t: ComposerTranslation,
  detail?: { event_type?: string | number; event_desc?: string; attack_technique?: string },
): string {
  if (!detail) return '--'
  const typeName =
    resolveSecurityEventTypeLabel(detail.event_type, (detail as { event_id?: number }).event_id) ??
    trEventType(t, detail.event_type)
  if (typeName !== '--' && !/^\d+$/.test(typeName)) return typeName
  const desc = detail.event_desc?.trim()
  if (desc) {
    const short = desc.split('/')[0]?.trim()
    if (short) return short
    return desc.length > 40 ? `${desc.slice(0, 40)}...` : desc
  }
  if (detail.attack_technique) return detail.attack_technique
  return typeName
}

/** API threat_type：1 文件 / 2 进程 / 3 IP / 4 行为哈希 / 5 URL / 6 用户 */
export function trThreatEntityType (t: ComposerTranslation, apiType?: number) {
  if (apiType == null) return '--'
  const key = `securityEvent.threatEntityType.${apiType}`
  const text = t(key)
  return text !== key ? text : '--'
}

/** 列表威胁类型：优先接口 threat_type，否则从威胁实体推断 */
export function trListThreatType (
  t: ComposerTranslation,
  row: Pick<SecurityEventListItem, 'threat_type' | 'threat_entity'>,
) {
  const apiType = row.threat_type ?? inferApiThreatTypeFromEntity(row.threat_entity)
  return trThreatEntityType(t, apiType)
}

export function threatEntityTypeTagType (apiType?: number): 'success' | 'info' | 'warning' | 'danger' | undefined {
  if (apiType === ApiThreatType.Ip || apiType === ApiThreatType.Url) return 'danger'
  if (apiType === ApiThreatType.Process || apiType === ApiThreatType.File) return 'warning'
  return 'info'
}

export function trHandleResult (t: ComposerTranslation, r?: number) {
  if (r == null) return '--'
  // 优先 apiHandleResult（1=已阻断/2=已隔离/3=已告警），其次 disposeResult（0=已放行…6=添加信任）
  const prdKey = `securityEvent.disposeResult.${r}`
  if (t(prdKey) !== prdKey) return t(prdKey)
  const apiKey = `securityEvent.apiHandleResult.${r}`
  if (t(apiKey) !== apiKey) return t(apiKey)
  return '--'
}

/**
 * 列表/详情处置结果摘要。
 * handle_result 为 SecurityDisposeResult 枚举（0-已放行…6-添加信任）。
 * 仅当显式传入 handleResults 数组时按多结果汇总。
 */
export function trHandleResultsSummary (
  t: ComposerTranslation,
  handleResult?: number,
  handleResults?: number[],
): string {
  if (handleResults?.length) {
    const codes = parseHandleResultCodes(undefined, handleResults)
    if (!codes.length) return '--'
    return codes.map((c) => trHandleResult(t, c)).join('、')
  }
  if (handleResult == null) return '--'
  return trHandleResult(t, handleResult)
}

export function trAttackTactic (t: ComposerTranslation, tactic?: string | number) {
  if (tactic == null || tactic === '') return '--'
  return getAttackTacticName(tactic, t) ?? String(tactic)
}

export function eventTypeOptions (t: ComposerTranslation) {
  return getEventTypeFilterOptions().map((opt) => ({
    ...opt,
    label: trEventType(t, opt.value),
  }))
}

export function levelOptions (t: ComposerTranslation) {
  return [
    { label: trFilterEventLevel(t, SecurityEventLevel.Low), value: SecurityEventLevel.Low },
    { label: trFilterEventLevel(t, SecurityEventLevel.Medium), value: SecurityEventLevel.Medium },
    { label: trFilterEventLevel(t, SecurityEventLevel.High), value: SecurityEventLevel.High },
  ]
}

export function disposeStatusOptions (t: ComposerTranslation) {
  return [
    { label: trDisposeStatus(t, SecurityDisposeStatus.Pending), value: SecurityDisposeStatus.Pending },
    { label: trDisposeStatus(t, SecurityDisposeStatus.Trusted), value: SecurityDisposeStatus.Trusted },
    { label: trDisposeStatus(t, SecurityDisposeStatus.Handled), value: SecurityDisposeStatus.Handled },
    { label: trDisposeStatus(t, SecurityDisposeStatus.Ignored), value: SecurityDisposeStatus.Ignored },
  ]
}

export function handleResultOptions (t: ComposerTranslation) {
  return [
    { label: t('securityEvent.filterAll'), value: -1 },
    ...Object.values(SecurityDisposeResult)
      .filter((v): v is SecurityDisposeResult => typeof v === 'number')
      .map((v) => ({ label: trHandleResult(t, v), value: v })),
  ]
}

export function attackTacticOptions (t: ComposerTranslation) {
  return getAttackTacticFilterOptions(t)
}

export function truncateText (text: string, max = 50) {
  if (!text) return '--'
  return text.length > max ? `${text.slice(0, max)}...` : text
}

const HANDLE_LOG_ACTIONS_WITH_DETAIL = new Set<number>([
  SafeEventHandleLogAction.Block,
  SafeEventHandleLogAction.Trust,
  SafeEventHandleLogAction.Isolate,
])

/** 从 API content 提取【】内详情，供 handleLogAction 模板占位 */
export function normalizeHandleLogDetail (content?: string): string {
  const raw = content?.trim()
  if (!raw) return ''
  const bracketMatch = raw.match(/[【\[]([^】\]]+)[】\]]/)
  if (bracketMatch) return `【${bracketMatch[1]}】`
  if (raw.startsWith('【') || raw.startsWith('[')) return raw
  return `【${raw}】`
}

/**
 * 解析处置日志动作码：优先用 data_type；
 * 兼容后端把动作码内嵌在 content 开头的情况（如 "4【C:\\...】"）。
 */
function resolveHandleLogActionCode (operateType?: number, content?: string): number {
  const type = Number(operateType)
  if (Number.isFinite(type) && type >= 1 && type <= 7) return type
  const raw = content?.trim() ?? ''
  const leadingCode = raw.match(/^\s*(\d+)\s*(?:[【\[]|$)/)
  if (leadingCode) {
    const code = Number(leadingCode[1])
    if (Number.isFinite(code) && code >= 1 && code <= 7) return code
  }
  return NaN
}

/** 按 log.data_type 映射处置动作文案 */
export function trHandleLogActionText (
  t: ComposerTranslation,
  operateType?: number,
  content?: string,
): string {
  const type = resolveHandleLogActionCode(operateType, content)
  if (!Number.isFinite(type)) {
    return content?.trim() || '--'
  }
  const key = `securityEvent.handleLogAction.${type}`
  const translated = t(key)
  if (translated === key) return content?.trim() || '--'
  if (HANDLE_LOG_ACTIONS_WITH_DETAIL.has(type)) {
    return t(key, { content: normalizeHandleLogDetail(content) })
  }
  return translated
}

/** 处置时间线单行：{operator}执行了操作：{action} */
export function formatDisposeTimelineEntry (
  t: ComposerTranslation,
  log: SafeEventDetailLog,
): string {
  const operator = log.operator?.trim() || '--'
  const action = trHandleLogActionText(t, log.operate_type, log.content)
  return t('securityEvent.timelineEntry', { operator, action })
}

/** @deprecated 使用 trListEventLevel */
export const trEventLevel = trFilterEventLevel
/** @deprecated 使用 listLevelColor */
export const levelColor = listLevelColor
/** @deprecated 使用 trHandleResult */
export const trDisposeResult = trHandleResult
/** @deprecated 使用 attackTacticOptions */
export const attackStageOptions = attackTacticOptions
/** @deprecated 使用 handleResultOptions */
export const disposeResultOptions = handleResultOptions
/** @deprecated 使用 trAttackTactic */
export const trAttackStage = trAttackTactic
